/**
 * Tipos e helpers partilhados entre o banner da homepage (banner-eclipse-2026) e o
 * artigo interactivo (eclipse-iberico-2026) para o eclipse solar de 12 de agosto de
 * 2026 — ver Features/Interactive/Instances/EclipseIberico2026/DATA.md para
 * proveniência.
 *
 * As duas peças consomem ficheiros DIFERENTES, gerados da mesma corrida do mesmo
 * motor (por isso não se podem contradizer):
 *
 * - `concelhos.json` (278 KB / 35 KB gz, 308 concelhos) — só o interactivo. Lá a
 *   granularidade ao concelho é a peça, e paga-se uma vez numa página que o leitor
 *   escolheu abrir.
 * - `distritos.json` (5,8 KB / 1,3 KB gz, 20 distritos) — só o banner. O banner é um
 *   banner widget e renderiza em TODAS as páginas do site: com o ficheiro dos
 *   concelhos, cada página vista pagava 35 KB transferidos e 278 KB de parse na main
 *   thread para preencher um `<select>`.
 *
 * Nada aqui faz aritmética de fusos: todas as horas chegam já formatadas em
 * pt-PT (`label`) e com offset explícito (`iso`), calculadas em build-time pelo
 * gerador. O browser só lê `utc`/`iso` para posicionar o instante numa recta do
 * tempo — nunca para decidir a que horas do dia isso corresponde em Lisboa.
 */

export const ECLIPSE_SCHEMA_VERSION = 1;

export interface EclipseEventTime {
    iso: string;
    utc: string;
    label: string;
    sunAltitude?: number;
}

export interface TotalityInfo {
    grazing: boolean;
    maxDurationSeconds: number;
    medianDurationSeconds: number;
    maxDurationPoint: { lat: number; lon: number } | null;
    footprintKm2: number;
    areaLabel: string;
    note: string;
}

export interface ConcelhoRecord {
    dtmnfr: string;
    nome: string;
    slug: string;
    distrito: string;
    region: 'continente' | 'madeira' | 'acores';
    sede: { lat: number; lon: number; source: string };
    tz: string;
    visible: boolean;
    eclipseKind: 'partial' | 'annular' | 'total';
    obscurationPct: number;
    obscurationLabel: string;
    partialBegin: EclipseEventTime;
    peak: EclipseEventTime;
    partialEnd: EclipseEventTime;
    /** Usar SEMPRE isto para a hora de fim mostrada ao leitor, nunca partialEnd:
     *  já é min(partialEnd, sunset) — ver DATA.md, "Sol põe-se antes do fim". */
    displayEnd: EclipseEventTime;
    durationSeconds: number;
    sunSetsBeforeEnd: boolean;
    sunset?: EclipseEventTime;
    totality?: TotalityInfo;
}

export interface DistritoRecord {
    code: string;
    nome: string;
    slug: string;
    region: 'continente' | 'madeira' | 'acores';
    concelhoCodes: string[];
}

interface RangeSummary {
    firstUtc: string;
    lastUtc: string;
    firstLabel: string;
    lastLabel: string;
    firstCode: string;
    lastCode: string;
}

export interface NationalSummary {
    concelhos: number;
    obscurationPct: { min: number; minCode: string; minLabel: string; max: number; maxCode: string; maxLabel: string };
    partialBegin: RangeSummary;
    peak: RangeSummary;
    partialEnd: RangeSummary;
    sunSetsBeforeEndCodes: string[];
}

export interface EclipseDoc {
    meta: { version: number; generatedAt: string };
    event: {
        id: string;
        dateLabel: string;
        national: NationalSummary;
        regions: { madeira: NationalSummary; acores: NationalSummary };
        totalityLimit: {
            touchesCodes: string[];
            maxDurationSeconds: number;
            medianDurationSeconds: number;
            maxDurationPoint: { lat: number; lon: number } | null;
            areaLabel: string;
            note: string;
        };
    };
    distritos: DistritoRecord[];
    concelhos: Record<string, ConcelhoRecord>;
}

/**
 * Um distrito no ficheiro do banner (`distritos.json`). Os números são os da **sede**
 * do distrito, não uma média nem um valor do distrito — o banner tem de os rotular
 * como tal ("98,1% na cidade do Porto"), porque a gama interna de um distrito chega a
 * 2 pp no continente e 5,1 pp nos Açores. `obscurationMin`/`Max` são essa gama, para
 * a aproximação ser auditável sem reabrir o concelhos.json.
 *
 * Nas ilhas a sede é escolhida à mão (Funchal, Ponta Delgada): não existe um concelho
 * "Madeira" nem "Açores". Ver generate-distritos.mjs.
 */
export interface DistritoBannerRecord {
    code: string;
    nome: string;
    region: 'continente' | 'madeira' | 'acores';
    sede: string;
    sedeCode: string;
    obscurationPct: number;
    obscurationLabel: string;
    obscurationMin: number;
    obscurationMax: number;
    partialBegin: { utc: string; label: string };
    peak: { utc: string; label: string };
    displayEnd: { utc: string; label: string };
    sunSetsBeforeEnd: boolean;
    totality?: TotalityInfo;
}

export interface EclipseDistritosDoc {
    meta: { version: number; generatedAt: string };
    distritos: DistritoBannerRecord[];
}

let inFlight: Promise<EclipseDoc> | null = null;

/**
 * Fetch com cache em memória (um só pedido por carregamento de página, mesmo que
 * vários componentes o chamem) e validação da versão do schema. `cache: 'force-cache'`
 * porque o URL já traz `?__obsver=<release>` — o ficheiro é imutável por essa chave.
 */
export function fetchEclipseData(url: string): Promise<EclipseDoc> {
    if (inFlight) return inFlight;
    inFlight = fetch(url, { cache: 'force-cache' })
        .then(res => {
            if (!res.ok) throw new Error(`eclipse2026: HTTP ${res.status} em ${url}`);
            return res.json() as Promise<EclipseDoc>;
        })
        .then(doc => {
            if (doc?.meta?.version !== ECLIPSE_SCHEMA_VERSION) {
                throw new Error(`eclipse2026: schema version ${doc?.meta?.version}, esperado ${ECLIPSE_SCHEMA_VERSION}`);
            }
            return doc;
        })
        .catch(err => {
            inFlight = null; // não fica preso num erro — a próxima chamada tenta de novo
            throw err;
        });
    return inFlight;
}

let inFlightDistritos: Promise<EclipseDistritosDoc> | null = null;

/** O mesmo contrato de `fetchEclipseData`, para o ficheiro do banner. Separado e não
 *  parametrizado porque a cache em memória é por ficheiro, não por chamada. */
export function fetchEclipseDistritos(url: string): Promise<EclipseDistritosDoc> {
    if (inFlightDistritos) return inFlightDistritos;
    inFlightDistritos = fetch(url, { cache: 'force-cache' })
        .then(res => {
            if (!res.ok) throw new Error(`eclipse2026: HTTP ${res.status} em ${url}`);
            return res.json() as Promise<EclipseDistritosDoc>;
        })
        .then(doc => {
            if (doc?.meta?.version !== ECLIPSE_SCHEMA_VERSION) {
                throw new Error(`eclipse2026: schema version ${doc?.meta?.version}, esperado ${ECLIPSE_SCHEMA_VERSION}`);
            }
            return doc;
        })
        .catch(err => {
            inFlightDistritos = null;
            throw err;
        });
    return inFlightDistritos;
}

const STORAGE_KEY = 'obs_eclipse_2026';

interface StoredChoice {
    v: number;
    code: string;
    savedAt: number;
}

/** Nunca deixa uma leitura/escrita de localStorage rebentar o widget (Safari privado atira). */
export function loadSavedConcelho(): string | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<StoredChoice>;
        if (parsed?.v !== 1 || typeof parsed.code !== 'string') return null;
        return parsed.code;
    } catch {
        return null;
    }
}

/**
 * O código de distrito de um `dtmnfr` de concelho — o que permite ao banner (que só
 * conhece distritos) aproveitar a escolha que o leitor fez no interactivo (que guarda
 * concelhos) sob a MESMA chave de localStorage, sem ter de embarcar o mapa dos 308.
 *
 * Os dois primeiros dígitos são o distrito, **excepto nas ilhas**: os `dtmnfr`
 * insulares usam 31/32 (Madeira) e 41-49 (Açores), enquanto os distritos são 30 e 40.
 * Verificado contra os 308 concelhos: a regra acerta em todos.
 *
 * Aceita já um código de distrito (2 dígitos) e devolve-o tal e qual, porque é o que o
 * próprio banner guarda.
 */
export function distritoCodeFor(code: string): string | null {
    if (/^[0-9]{2}$/.test(code)) return code;
    if (!/^[0-9]{4}$/.test(code)) return null;
    const p = code.slice(0, 2);
    if (p === '31' || p === '32') return '30';
    if (p >= '41' && p <= '49') return '40';
    return p;
}

export function saveConcelho(code: string): void {
    try {
        const value: StoredChoice = { v: 1, code, savedAt: Date.now() };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
        // localStorage indisponível (privado/quota) — a escolha só dura a sessão, aceitável.
    }
}
